import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@tempo-io/sui-accordion';
import { cn } from '@tempo-io/sui-class-utils';
import { Dependency } from '@tempo-io/sui-icons';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@tempo-io/sui-tablelayout';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import type { IdentifiableTItem, ItemDetails } from '@/components/views/published/types';
import type { Item } from '@/lib/api/types/items';
import { DateFormat, formatLocalDate } from '@/lib/utils/common/dateFormats';

interface DependencyRow {
  id: number;
  name: string;
  itemTypeId?: number;
  itemTypeIcon?: string;
  itemTypeColor?: string;
  statusName?: string;
  statusColor?: string;
  expectedFinishDateTime: string | null;
}

interface ScrollableTableProps {
  columns: { id: string; header: string; cell: (row: DependencyRow) => React.ReactNode }[];
  rows: DependencyRow[];
}

const ScrollableTable = ({ columns, rows }: ScrollableTableProps) => {
  const { t } = useTranslation();
  return (
    <div className="max-h-[15rem] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.id} className="h-10 px-2 font-medium text-text-neutral-primary">
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center text-text-neutral-secondary"
              >
                {t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.noDependencies)}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn('border-b border-border-neutral last:!border-b')}
              >
                {columns.map((col) => (
                  <TableCell key={col.id} className="p-2">
                    {col.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

interface SectionProps {
  value: 'predecessors' | 'successors';
  title: string;
  description?: string;
  columns: { id: string; header: string; cell: (row: DependencyRow) => React.ReactNode }[];
  rows: DependencyRow[];
}

const DependencySection = ({ value, title, description, columns, rows }: SectionProps) => (
  <AccordionItem
    value={value}
    className="rounded-lg border border-border-neutral bg-bg-surface-base px-4 last:border"
  >
    <AccordionTrigger className="text-sm font-semibold leading-5 text-text-neutral-primary hover:no-underline">
      <span className="flex flex-1 items-start gap-2">
        <span className="flex-1">
          {title} ({rows.length})
          {!!description && (
            <span className="mt-1 block text-sm font-normal leading-5 text-text-neutral-secondary">
              {description}
            </span>
          )}
        </span>
      </span>
    </AccordionTrigger>
    <AccordionContent className="pt-2">
      <ScrollableTable columns={columns} rows={rows} />
    </AccordionContent>
  </AccordionItem>
);

interface PublishedDependenciesProps {
  item: Item;
  allItemDetails: Record<string, ItemDetails>;
  enrichedItems: Record<string, IdentifiableTItem>;
}

export const PublishedDependencies = ({
  item,
  allItemDetails,
  enrichedItems,
}: PublishedDependenciesProps) => {
  const { t } = useTranslation();

  const { predecessors, successors } = useMemo(() => {
    const preds: DependencyRow[] = [];
    const succs: DependencyRow[] = [];

    for (const dep of item.dependencies) {
      const details = allItemDetails[String(dep.dependencyItemId)];
      const enriched = enrichedItems[String(dep.dependencyItemId)];

      const row: DependencyRow = {
        id: dep.dependencyItemId,
        name: details?.name ?? String(dep.dependencyItemId),
        itemTypeId: details?.itemTypeId,
        itemTypeIcon: enriched?.itemTypeIcon,
        itemTypeColor: enriched?.itemTypeColor,
        statusName: enriched?.statusName,
        statusColor: enriched?.statusColor,
        expectedFinishDateTime: details?.schedule?.expectedFinishDateTime ?? null,
      };

      if (dep.dependencyType === 'PREDECESSOR') {
        preds.push(row);
      } else {
        succs.push(row);
      }
    }

    return { predecessors: preds, successors: succs };
  }, [item.dependencies, allItemDetails, enrichedItems]);

  const nameCell = (row: DependencyRow) => (
    <span className="flex items-center gap-2">
      {row.itemTypeId !== undefined && (
        <ItemTypeIcon
          itemType={{
            id: row.itemTypeId,
            name: row.name,
            description: null,
            itemLevelId: 0,
            color: row.itemTypeColor ?? null,
            icon: row.itemTypeIcon ?? null,
            createdAt: '',
            updatedAt: '',
            createdBy: '',
            updatedBy: '',
          }}
        />
      )}
      <span className="flex-1 min-w-0 truncate font-medium leading-5 text-text-neutral-primary">
        {row.name}
      </span>
    </span>
  );

  const statusCell = (row: DependencyRow) =>
    row.statusName ? (
      <ItemStatusBadge color={row.statusColor ?? ''} label={row.statusName} />
    ) : null;

  const finishCell = (row: DependencyRow) =>
    row.expectedFinishDateTime
      ? formatLocalDate(row.expectedFinishDateTime, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
      : '-';

  const predecessorColumns = [
    {
      id: 'waitingOn',
      header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.waitingOn),
      cell: nameCell,
    },
    {
      id: 'status',
      header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.status),
      cell: statusCell,
    },
    {
      id: 'expectedFinish',
      header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.expectedFinish),
      cell: finishCell,
    },
  ];

  const successorColumns = [
    {
      id: 'completionEnables',
      header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.completionEnables),
      cell: nameCell,
    },
    {
      id: 'successorStatus',
      header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.status),
      cell: statusCell,
    },
    {
      id: 'successorExpectedFinish',
      header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.expectedFinish),
      cell: finishCell,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center gap-1 text-lg font-bold text-text-neutral-secondary">
        <Dependency /> {t(($) => $.adaptivePlanner.itemPanel.dependencies)}
      </div>
      <Accordion
        type="multiple"
        defaultValue={['predecessors', 'successors']}
        className="flex flex-col gap-4"
      >
        <DependencySection
          value="predecessors"
          title={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessors)}
          description={t(
            ($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessorsDescription,
          )}
          columns={predecessorColumns}
          rows={predecessors}
        />
        <DependencySection
          value="successors"
          title={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successors)}
          description={t(
            ($) => $.adaptivePlanner.itemPanel.dependenciesSection.successorsDescription,
          )}
          columns={successorColumns}
          rows={successors}
        />
      </Accordion>
    </div>
  );
};
